/**
 * CORE-1735 — design tokens of the Live room (in-room screen, Desktop Web).
 *
 * Source of every value: Confluence AM space
 *   110428306  «Экран ведущего» — «Актуальная компактная компоновка · 16.10.2026»
 *              and «Светлая тема — точные параметры» (sizes, palette, shadows);
 *   110756133  «Счётчики» (counter ring gradient, fills, digits) — id as cited in
 *              CORE-1735; CORE-1733 sources give 110756133 to the backgrounds
 *              article below, so one of the two citations needs rechecking;
 *   page / header / surface backgrounds — CORE-1733: not Live's own values any
 *              more, they come from src/styles/platformBackgrounds.ts (Confluence
 *              110756133 «Agapella Web — общие фоны и поверхности», v4), the one
 *              source the whole platform paints from;
 *   111280168  «Выпадающие меню» (glass material contract v1, slider / toggle gradient).
 *
 * Slice 1 renders only `liveTokens.dark`; `liveTokens.light` is defined with the
 * same shape so the light slice flips a single switch (`LIVE_FORCE_DARK` in
 * src/components/live/useLiveTheme.ts). CORE-1733 (shared platform backgrounds)
 * maps the page / header / surface keys onto platformBackgrounds instead of a
 * second, Live-only palette; video, scrims, speaker glow, controls, focus and
 * badges keep their own Live values.
 *
 * Gradients and shadows are CSS strings for web-only style props
 * (`backgroundImage`, `boxShadow`); components use them behind `Platform.OS === 'web'`.
 */

import { platformBackgrounds } from './platformBackgrounds';

export type LiveThemeName = 'dark' | 'light';

/** Geometry in CSS px. Shared by both themes. */
export const LIVE_SIZES = {
  sidebarWidth: 400,
  sidebarWidthNarrow: 360,
  /** Viewport width below which the sidebar drops to `sidebarWidthNarrow`. */
  sidebarNarrowBelow: 1280,
  sidebarPadding: 12,
  tabsMinHeight: 56,
  sidebarRadius: 24, // slice 11 (D-86): rounded card on desktop
  /** Slice 11 (D-86): the sidebar card's gap to the right edge of the page. */
  sidebarCardRight: 8,

  toolbarHeight: 88,
  /** Slice 11 (D-86): the stage canvas is a rounded card inset from the page on desktop. */
  stageInset: 16,
  stageRadius: 16,
  controlSize: 48,
  controlIcon: 24,
  /** Slice 11 (D-87): the toolbar glyph box — the silhouettes draw ≈24px, half the control as on the mockup. */
  toolbarGlyph: 24,
  controlGap: 6,
  controlLabelSize: 12,
  controlLabelLine: 16,

  thumbW: 176,
  thumbH: 108,
  thumbRadius: 16,
  thumbGap: 12,
  thumbNameSize: 13,
  thumbNameLine: 18,
  statusIcon: 16,

  counterSize: 24,
  counterSizeWide: 28,
  counterRing: 1.5,
  // slice 10 (D-83): ease-in fade into the flat toolbar band; slice 17 (D-126): nine stops.
  // CORE-1917 p.8 (daily 28.10): 320 px covered half the picture in the light theme; the host article
  // asks for «примерно 120–160px», the fade only where the controls need it.
  scrimHeight: 160,
  toggleW: 44,
  toggleH: 26,
  toggleKnob: 22,

  /** «Сетка» tiles (slice 2, D-16). */
  tileRadius: 18,
  tileGap: 12,
  stagePadding: 12,
  tileNameSize: 14,
  tileNameLine: 20,
  /** Participant card (slice 2, D-20). */
  cardWidth: 460,
  cardRadius: 24,
  cardPadding: 24,
  cardAvatar: 64,
  // Slice 14 (D-108): the old card's geometry — 52 / 54 (primary) tall, radius 16.
  cardButtonHeight: 52,
  cardButtonPrimaryHeight: 54,
  cardButtonRadius: 16,
  /** Host article «Состояния и счётчики»: disabled .48 only on an unavailable action. */
  disabledOpacity: 0.48,
  /** Chat tab (slice 3, D-22..D-25). */
  systemRowMinHeight: 48,
  systemRowRadius: 12,
  systemIcon: 16, // slice 17 (D-130, QA V02): the article's 16px glyph
  chatAvatar: 28,
  composerRadius: 16,
  composerSend: 40,
  ctaRadius: 20,
  ctaBorderWidth: 2,
  ctaOrbit: 64,
  ctaIcon: 28,
  ctaButtonHeight: 44,
  /** Media stage (slice 4, practice article «Геометрия для реализации»). */
  miniCardW: 144,
  miniCardH: 88,
  miniGap: 8,
  miniNameSize: 12,
  miniNameLine: 16,
  miniStatusIcon: 14,
  quietPillW: 164,
  quietPillH: 44,
  quietPillRadius: 12,
  quietAvatar: 28,
  floatW: 176,
  floatH: 132,
  floatRadius: 16,
  floatInset: 16,
  floatMicZone: 24,
  /** CORE-1757 — keyboard moves of the floating window: an arrow, and with Shift. */
  floatKeyStep: 8,
  floatKeyStepBig: 32,
  /** Working value of the article; configurable, to be checked for comfort. */
  idleQuietMs: 5000,
  quietTransitionMs: 180,
  // Slice 9 (D-73): 4px line, two-row transport, 14px thumb with a white ring.
  progressHeight: 4,
  transportHeight: 72,
  transportThumb: 14,
  /** Dock menus and the device panel (slice 5, menus article «Параметры реализации» / «Настройки · устройства»). */
  menuRadius: 22,
  menuPadding: 20,
  menuGap: 12,
  /** Gap between the toolbar box and the popover above it. */
  menuAnchorGap: 8,
  /** Horizontal margin the popover keeps from the stage column edges. */
  menuMargin: 12,
  menuWidthPractice: 320,
  menuWidthBackground: 340,
  menuWidthVolume: 300,
  menuWidthDevice: 360,
  menuHeadingSize: 18,
  menuHeadingLine: 24,
  menuActionSize: 16,
  menuActionLine: 24,
  menuIcon: 20,
  menuChevron: 16,
  selectMinHeight: 44,
  selectRadius: 12,
  primaryMinHeight: 48,
  primaryRadius: 14,
  sliderTrack: 3,
  sliderThumb: 16,
  sliderHit: 44,
  /** Round transport controls inside the menus: background bed 40, practice 44. */
  menuRound: 40,
  menuTransport: 44,
  /** Modal family (slice 5b, menus article «Сменить медиа», «Диалоги времени, выхода…»). */
  modalWidth: 640,
  modalRadius: 24,
  modalPadding: 28,
  pickerPadding: 24,
  /** Horizontal / vertical viewport margins the modal keeps. */
  modalMargin: 16,
  modalMarginV: 24,
  dialogHeadingSize: 22,
  dialogHeadingLine: 28,
  dialogTextSize: 16,
  dialogTextLine: 24,
  dialogIcon: 28,
  dialogIconPad: 40,
  pickerTitleLine: 22,
  /** Fixed height of the picker's results area — the only scrolling part. */
  pickerListHeight: 360,
  candidateAvatar: 34,
  candidateListMax: 260,
  chipMinHeight: 32,
  tabMinHeight: 36,

  hitArea: 44,
  glowTransitionMs: 160,
  handPulseMs: 240,
  /** CORE-1759 — the soft brief highlight of «Участники» on a new arrival (static accent only under reduced motion). */
  arrivalPulseMs: 640,
  /** CORE-1757 — the late re-measure of the media canvas after a window resize / browser zoom (web). */
  resizeRemeasureMs: 120,
  focusWidth: 2,
  focusOffset: 3,
} as const;

export interface LiveTokens {
  name: LiveThemeName;
  pageBg: string;
  pageBgImage: string;
  headerBg: string;
  headerBgImage: string;
  headerBorder: string;
  /** Slice 9 (D-72): the vertical line after the logo. */
  headerDivider: string;
  surfaceBg: string;
  surfaceBgImage: string;
  surfaceBorder: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  heading: string;
  /** CORE-1851 p.1 — the halo behind a participant's name on a tile / thumbnail: readable over any video. */
  captionHalo: string;
  icon: string;
  iconOff: string;
  iconSlash: string;
  accent: string;
  accentText: string;
  peach: string;
  /** Text and glyph colour on accent / danger fills (power button, ON toggle knob). */
  onAccent: string;
  border: string;
  borderHover: string;
  focus: string;
  danger: string;
  dangerGradient: string;
  record: string;
  controlBg: string;
  controlBgImage: string;
  controlBgHover: string;
  controlBgActive: string;
  controlPressedBg: string;
  controlShadow: string;
  controlLabel: string;
  /** Slice 17 (D-120): the v4.2 ink of the toolbar glyphs per material — four diagonal stops for `base` / `accent`
   *  (rendered once as `live-ink-base` / `live-ink-accent` in LiveToolbar), solid colours for the rest. */
  glyphInk: { base: string[]; accent: string[]; off: string; neutral: string; record: string };
  /** Slice 11 (D-88): the control material of the icons article v4.2 («Материал контролов · точные значения»). */
  controlGlass: string;
  controlEdge: string;
  controlGlassShadow: string;
  controlSolid: string;
  controlEdgeHover: string;
  controlShadowHover: string;
  controlEdgeSelected: string;
  controlShadowSelected: string;
  controlShadowActive: string;
  controlEdgeRecording: string;
  controlShadowRecording: string;
  powerGradient: string;
  powerEdge: string;
  powerShadow: string;
  controlFocus: string;
  sidebarBorder: string;
  sidebarShadow: string;
  tabUnderline: string;
  counterFill: string;
  counterText: string;
  counterRing: string;
  toggleOn: string;
  toggleOff: string;
  toggleKnob: string;
  speakerBorder: string;
  speakerGlow: string;
  /** Stage fade into the toolbar band (slice 10, D-83): ends opaque in `pageBg`. */
  scrimImage: string;
  /** CORE-1917 — the scrim of the inset desktop card: same curve, ends at .78 (article: «не делать низ полностью непрозрачным»); the full-bleed canvas keeps the opaque end so the toolbar band has no edge. */
  scrimInsetImage: string;
  /** Caption over a camera picture (tile, thumbnail, floating window, mini card): the translucent slice 9 curve. */
  captionScrimImage: string;
  thumbBg: string;
  thumbBgImage: string;
  thumbBorder: string;
  chipBg: string;
  chipBorder: string;
  chipText: string;
  searchBg: string;
  searchBorder: string;
  searchPlaceholder: string;
  /** Participant card (slice 2): modal backdrop, card surface, primary (peach outline) button, avatar ring. */
  overlay: string;
  cardBg: string;
  cardBgImage: string;
  cardBorder: string;
  cardShadow: string;
  primaryBg: string;
  primaryBorder: string;
  primaryText: string;
  avatarRing: string;
  /** Chat tab (slice 3): compact system rows and the guest join block (guest article 110428433). */
  systemRowBg: string;
  systemRowBorder: string;
  ctaHeading: string;
  ctaBorder: string;
  ctaGlow: string;
  ctaButtonBg: string;
  ctaButtonText: string;
  ctaOrbitFrom: string;
  ctaOrbitTo: string;
  ctaIcon: string;
  /** Media stage (slice 4): practice progress, translucent overlays, floating window shadow. */
  progressTrack: string;
  progressFill: string;
  /** Slice 9 (D-73): horizontal gradient of the played part of the line. */
  progressFillImage: string;
  /** Slice 11 (D-89): the thumb fill (the white ring stays `onAccent`). */
  progressThumb: string;
  /** Slice 9 (D-76): see-through card / pill fill over the practice (with backdrop blur). */
  cardGlassBg: string;
  overlayScrim: string;
  floatShadow: string;
  /** Dock menus (slice 5): the glass material contract v1 of the menus article (111280168); slice 10 (D-82) lightened, no contrast variant. */
  glassBg: string;
  /** The device panel / media picker surface («live-overlay--clear»). Slice 13 (D-101, QA R1): opaque enough that `text` keeps 4.5:1 over any video frame under it. */
  glassBgClear: string;
  glassWarm: string;
  glassEdge: string;
  glassTop: string;
  glassShadow: string;
  /** Opaque surface when backdrop-filter is unavailable or transparency is reduced. */
  glassFallback: string;
  glassFocus: string;
  /** Heading / control glyphs inside the glass: peach in dark, warm brown in light. */
  glassIcon: string;
  glassSection: string;
  glassSelectBg: string;
  glassSelectBorder: string;
  glassCurrentBg: string;
  glassCurrentBorder: string;
  /** Shared gradient of every active control: toggles ON, pause, slider fill and thumb, primary rows. */
  menuActive: string;
  menuActiveHover: string;
  menuActivePressed: string;
  menuActiveText: string;
  menuActiveBorder: string;
  destructiveBg: string;
  destructiveBorder: string;
  destructiveText: string;
  /** Modal family (slice 5b): the single page scrim and the hand-over refusal box. */
  modalScrim: string;
  errorBg: string;
  errorText: string;
  /** Status banner text tones (slice 6): the old amber / green / red literals in dark, 4.5:1-safe values on cream in light. */
  toneWarn: string;
  toneSuccess: string;
  toneDanger: string;
  toneRecord: string;
}

const pbDark = platformBackgrounds.dark;
const pbLight = platformBackgrounds.light;

const dark: LiveTokens = {
  name: 'dark',
  // CORE-1733 — страница, шапка и поверхность комнаты — общие фоны платформы.
  pageBg: pbDark.pageBase,
  pageBgImage: pbDark.pageImage,
  headerBg: pbDark.headerBase,
  headerBgImage: pbDark.headerImage,
  headerBorder: pbDark.headerBorder,
  headerDivider: 'rgba(243,216,200,.18)',
  surfaceBg: pbDark.surfaceBase,
  surfaceBgImage: pbDark.surfaceImage,
  surfaceBorder: pbDark.surfaceBorder,
  text: '#C5C2BD',
  textSecondary: '#9D9D9F',
  textMuted: '#7C7B7D',
  heading: '#F5F0EB',
  captionHalo: 'rgba(0,0,0,.78)',
  icon: '#D7B7A4',
  iconOff: '#9D9D9F',
  iconSlash: '#EF7954', // slice 17 (D-120): the v4.2 dark diagonal
  accent: '#E98245',
  accentText: '#F0A06D',
  peach: '#F0A06D',
  onAccent: '#FFFDF9',
  border: 'rgba(240,160,109,.18)',
  borderHover: 'rgba(240,160,109,.32)',
  focus: '#E98245',
  danger: '#B94332',
  dangerGradient: 'linear-gradient(145deg,#D8785D 0%,#B94332 100%)',
  record: '#C74446',
  controlBg: 'rgba(255,255,255,.05)',
  controlBgImage: 'linear-gradient(180deg,rgba(255,255,255,.06),rgba(255,255,255,.02))',
  controlBgHover: 'rgba(255,255,255,.09)',
  controlBgActive: 'rgba(240,160,109,.16)',
  controlPressedBg: 'rgba(240,160,109,.22)',
  controlShadow: 'inset 0 1px 0 rgba(255,255,255,.08),0 3px 10px rgba(0,0,0,.28)',
  controlLabel: '#C5C2BD',
  glyphInk: { base: ['#FFF0DE', '#FCD9BD', '#F1B694', '#DF8F6B'], accent: ['#FFE6BE', '#FFD1A1', '#F7AA71', '#E88B4D'], off: '#C4ACA0', neutral: '#D1BDB0', record: '#EF7668' },
  controlGlass: 'linear-gradient(145deg,rgba(66,67,67,.36),rgba(30,32,34,.38))',
  controlEdge: 'rgba(240,207,181,.17)',
  controlGlassShadow: 'inset 0 -1px 0 rgba(193,104,57,.045),0 3px 9px rgba(0,0,0,.15)',
  controlSolid: '#292B2E',
  controlEdgeHover: 'rgba(240,160,109,.32)',
  controlShadowHover: 'inset 0 0 8px rgba(233,130,69,.20),0 3px 10px rgba(0,0,0,.15)',
  controlEdgeSelected: 'rgba(233,130,69,.78)',
  controlShadowSelected: 'inset 0 0 12px 1px rgba(233,130,69,.20),0 4px 12px rgba(0,0,0,.15)',
  controlShadowActive: 'inset 0 1px 4px rgba(0,0,0,.15)',
  controlEdgeRecording: 'rgba(199,68,70,.50)',
  controlShadowRecording: 'inset 0 0 10px rgba(199,68,70,.18),0 4px 12px rgba(0,0,0,.15)',
  powerGradient: 'linear-gradient(145deg,#ED784D,#D13921 60%,#9C271C)',
  powerEdge: 'rgba(255,255,255,.45)',
  powerShadow: 'inset 0 -2px 3px rgba(91,13,9,.22),0 4px 12px rgba(185,67,50,.18)',
  controlFocus: 'rgba(240,160,109,.85)',
  sidebarBorder: 'rgba(233,130,69,.42)',
  sidebarShadow: '-4px 0 8px rgba(0,0,0,.18)',
  tabUnderline: 'linear-gradient(90deg,#E98245 0%,#F0A06D 100%)',
  counterFill: 'rgba(33,29,35,.94)',
  counterText: '#FF6A2A',
  counterRing: 'linear-gradient(124.992deg,#FF7138,#FF9A62,#F6C5A7)',
  toggleOn: 'linear-gradient(125deg,#E98245 0%,#F0A06D 55%,#F6C5A7 100%)',
  toggleOff: '#34383B',
  toggleKnob: '#FFFDF9',
  speakerBorder: 'rgba(233,130,69,.78)',
  speakerGlow: 'inset 0 0 8px 2px rgba(240,160,109,.48),inset 0 0 20px 4px rgba(233,130,69,.20)',
  scrimImage:
    'linear-gradient(to bottom,rgba(32,34,38,0) 0%,rgba(32,34,38,.02) 12%,rgba(32,34,38,.07) 25%,rgba(32,34,38,.14) 37%,rgba(32,34,38,.24) 50%,rgba(32,34,38,.40) 62%,rgba(32,34,38,.60) 75%,rgba(32,34,38,.82) 88%,rgba(32,34,38,1) 100%)',
  scrimInsetImage:
    'linear-gradient(to bottom,rgba(32,34,38,0) 0%,rgba(32,34,38,.02) 12%,rgba(32,34,38,.05) 25%,rgba(32,34,38,.11) 37%,rgba(32,34,38,.19) 50%,rgba(32,34,38,.31) 62%,rgba(32,34,38,.47) 75%,rgba(32,34,38,.64) 88%,rgba(32,34,38,.78) 100%)',
  captionScrimImage:
    'linear-gradient(to bottom,rgba(32,34,38,0) 0%,rgba(32,34,38,.18) 35%,rgba(32,34,38,.52) 72%,rgba(32,34,38,.78) 100%)',
  thumbBg: 'rgba(32,34,38,.72)',
  thumbBgImage: 'linear-gradient(135deg,rgba(41,43,46,.9) 0%,rgba(37,39,42,.9) 52%,rgba(48,45,43,.9) 100%)',
  thumbBorder: 'rgba(241,181,142,.16)',
  chipBg: 'rgba(240,160,109,.14)',
  chipBorder: 'rgba(240,160,109,.28)',
  chipText: '#F0A06D',
  searchBg: 'rgba(255,255,255,.04)',
  searchBorder: 'rgba(240,160,109,.18)',
  searchPlaceholder: '#7C7B7D',
  overlay: 'rgba(10,10,14,.55)',
  cardBg: '#202226',
  cardBgImage: 'radial-gradient(ellipse at 50% 0%,rgba(233,130,69,.08),transparent 55%),linear-gradient(180deg,#25272A 0%,#202226 100%)',
  cardBorder: 'rgba(240,160,109,.18)',
  cardShadow: '0 24px 70px rgba(0,0,0,.45),0 4px 18px rgba(0,0,0,.2)',
  primaryBg: 'linear-gradient(145deg,rgba(240,160,109,.18) 0%,rgba(233,130,69,.10) 100%)',
  primaryBorder: 'rgba(233,130,69,.48)',
  primaryText: '#F0A06D',
  avatarRing: 'rgba(240,160,109,.55)',
  systemRowBg: 'rgba(255,255,255,.035)',
  systemRowBorder: 'rgba(240,160,109,.10)',
  ctaHeading: '#DDD5CD',
  ctaBorder: 'linear-gradient(125deg,#F27B3F,#F4AD72)',
  ctaGlow: '0 0 8px rgba(242,123,63,.12),inset 0 0 14px rgba(244,173,114,.06)',
  ctaButtonBg: 'linear-gradient(135deg,#FFC493 0%,#F5A164 100%)',
  ctaButtonText: '#332A25',
  ctaOrbitFrom: '#E98245',
  ctaOrbitTo: '#F6C5A7',
  ctaIcon: '#F0A06D',
  progressTrack: 'rgba(255,255,255,.28)',
  progressFill: '#EB8F49', // slice 11 (D-89): the mockup orange
  progressFillImage: 'linear-gradient(90deg,#E98843,#EE9552)',
  progressThumb: '#FE9152',
  cardGlassBg: 'rgba(32,34,38,.42)',
  overlayScrim: 'rgba(32,34,38,.72)',
  floatShadow: '0 10px 28px rgba(0,0,0,.42),0 2px 8px rgba(0,0,0,.24)',
  glassBg: 'rgba(32,34,38,.60)',
  glassBgClear: 'rgba(32,34,38,.82)',
  glassWarm: 'rgba(233,130,69,.055)',
  glassEdge: 'rgba(242,161,112,.42)',
  glassTop: 'rgba(255,255,255,.075)',
  glassShadow: '0 12px 32px rgba(0,0,0,.20)',
  glassFallback: '#252629',
  glassFocus: '#F3D8C8',
  glassIcon: '#F0A06D',
  glassSection: '#F6C5A7',
  glassSelectBg: 'rgba(255,255,255,.035)',
  glassSelectBorder: 'rgba(241,181,142,.20)',
  glassCurrentBg: 'rgba(233,130,69,.10)',
  glassCurrentBorder: 'rgba(240,160,109,.28)',
  menuActive: 'linear-gradient(125deg,#E98245 0%,#F0A06D 55%,#F6C5A7 100%)',
  menuActiveHover: 'linear-gradient(125deg,#ED8E54 0%,#F3AA79 55%,#F8CFB5 100%)',
  menuActivePressed: 'linear-gradient(125deg,#DA733A 0%,#E9945F 55%,#EDB28F 100%)',
  menuActiveText: '#332A25',
  menuActiveBorder: 'rgba(246,197,167,.45)',
  destructiveBg: 'rgba(180,90,65,.08)',
  destructiveBorder: 'rgba(230,146,118,.55)',
  destructiveText: '#F3B39A',
  modalScrim: 'rgba(0,0,0,.12)',
  errorBg: 'rgba(245,158,11,.12)',
  errorText: '#F5B14B',
  toneWarn: '#F59E0B',
  toneSuccess: '#22C55E',
  toneDanger: '#EF4444',
  toneRecord: '#DC2626',
};

const light: LiveTokens = {
  name: 'light',
  // CORE-1733 — светлая Live — сливочная v4 платформы: прежние Live-only слои
  // (два radial, шапка #FFF8F2, поверхность #FFF8F2→#FBF1E8) заменены общими.
  pageBg: pbLight.pageBase,
  pageBgImage: pbLight.pageImage,
  headerBg: pbLight.headerBase,
  headerBgImage: pbLight.headerImage,
  headerBorder: pbLight.headerBorder,
  headerDivider: 'rgba(226,151,105,.30)',
  surfaceBg: pbLight.surfaceBase,
  surfaceBgImage: pbLight.surfaceImage,
  surfaceBorder: pbLight.surfaceBorder,
  text: '#332A25',
  textSecondary: '#70645D',
  // Slice 6 review: #877971 measured 3.9:1 on cream; corrected for the 4.5:1 rule (D-60).
  textMuted: '#76685F',
  heading: '#2A211C',
  captionHalo: 'rgba(255,253,249,.9)',
  icon: '#91634D',
  iconOff: '#877971',
  iconSlash: '#C7442B', // v4.2 light diagonal
  accent: '#E98245',
  accentText: '#9A421F',
  peach: '#F0A06D',
  onAccent: '#FFFDF9',
  border: 'rgba(226,151,105,.16)',
  borderHover: 'rgba(226,151,105,.30)',
  focus: 'rgba(154,66,31,.65)',
  danger: '#B94332',
  dangerGradient: 'linear-gradient(145deg,#D8785D 0%,#B94332 100%)',
  record: '#C74446',
  controlBg: 'rgba(51,42,37,.05)',
  controlBgImage: 'linear-gradient(180deg,rgba(255,255,255,.7),rgba(255,255,255,.3))',
  controlBgHover: 'rgba(51,42,37,.08)',
  controlBgActive: 'rgba(233,130,69,.14)',
  controlPressedBg: 'rgba(233,130,69,.20)',
  controlShadow: 'inset 0 1px 0 rgba(255,255,255,.8),0 3px 10px rgba(51,42,37,.08)',
  controlLabel: '#332A25',
  glyphInk: { base: ['#FFB88D', '#F27F58', '#D74C28', '#A83019'], accent: ['#FFD293', '#FFAA56', '#F47D28', '#D75319'], off: '#A98D80', neutral: '#A18A7A', record: '#E45B50' },
  controlGlass: 'radial-gradient(ellipse at 20% 0%,rgba(255,255,255,.90),transparent 67%),linear-gradient(145deg,rgba(255,255,255,.64),rgba(255,249,242,.34))',
  // CORE-1917 follow-up (owner, 30.10): the resting edge of the round dock controls reads too faint on the cream panel; .17 → .28, hover keeps a step above.
  controlEdge: 'rgba(226,151,105,.28)',
  controlGlassShadow: 'inset 0 1px 0 rgba(255,255,255,.95),inset 0 -1px 0 rgba(193,104,57,.045),0 3px 9px rgba(117,57,28,.065)',
  controlSolid: '#FFF8F2',
  controlEdgeHover: 'rgba(226,151,105,.38)',
  controlShadowHover: 'inset 0 1px 0 rgba(255,255,255,.95),inset 0 0 8px rgba(233,130,69,.16),0 3px 10px rgba(117,57,28,.065)',
  controlEdgeSelected: 'rgba(233,130,69,.78)',
  controlShadowSelected: 'inset 0 1px 0 rgba(255,255,255,.95),inset 0 0 12px 1px rgba(233,130,69,.16),0 4px 12px rgba(117,57,28,.065)',
  controlShadowActive: 'inset 0 1px 4px rgba(117,57,28,.065)',
  controlEdgeRecording: 'rgba(199,68,70,.50)',
  controlShadowRecording: 'inset 0 1px 0 rgba(255,255,255,.95),inset 0 0 10px rgba(199,68,70,.18),0 4px 12px rgba(117,57,28,.065)',
  powerGradient: 'radial-gradient(ellipse at 25% 0%,rgba(255,191,143,.55),transparent 65%),linear-gradient(145deg,#ED784D,#D13921 60%,#9C271C)',
  powerEdge: 'rgba(255,255,255,.45)',
  powerShadow: 'inset 0 1px 0 rgba(255,232,201,.65),inset 0 -2px 3px rgba(91,13,9,.22),0 4px 12px rgba(185,67,50,.18)',
  controlFocus: 'rgba(154,66,31,.65)',
  sidebarBorder: 'rgba(226,151,105,.38)',
  sidebarShadow: '-4px 0 8px rgba(51,42,37,.035)',
  tabUnderline: 'linear-gradient(90deg,#E98245 0%,#F0A06D 100%)',
  counterFill: '#FFFDF9',
  // Slice 6 review: #E98245 on the cream fill measured 2.7:1 for 12px digits (D-60).
  counterText: '#9A421F',
  counterRing: 'linear-gradient(124.992deg,#FF7138,#FF9A62,#F6C5A7)',
  toggleOn: 'linear-gradient(125deg,#E98245 0%,#F0A06D 55%,#F6C5A7 100%)',
  toggleOff: '#E7E2DC',
  toggleKnob: '#FFFDF9',
  speakerBorder: 'rgba(233,130,69,.78)',
  speakerGlow: 'inset 0 0 8px 2px rgba(240,160,109,.48),inset 0 0 20px 4px rgba(233,130,69,.20)',
  scrimImage:
    'linear-gradient(to bottom,rgba(252,245,231,0) 0%,rgba(252,245,231,.02) 12%,rgba(252,245,231,.07) 25%,rgba(252,245,231,.14) 37%,rgba(252,245,231,.24) 50%,rgba(252,245,231,.40) 62%,rgba(252,245,231,.60) 75%,rgba(252,245,231,.82) 88%,rgba(252,245,231,1) 100%)',
  scrimInsetImage:
    'linear-gradient(to bottom,rgba(252,245,231,0) 0%,rgba(252,245,231,.02) 12%,rgba(252,245,231,.05) 25%,rgba(252,245,231,.11) 37%,rgba(252,245,231,.19) 50%,rgba(252,245,231,.31) 62%,rgba(252,245,231,.47) 75%,rgba(252,245,231,.64) 88%,rgba(252,245,231,.78) 100%)',
  captionScrimImage:
    'linear-gradient(to bottom,rgba(255,253,249,0) 0%,rgba(255,253,249,.12) 35%,rgba(255,253,249,.48) 72%,rgba(255,253,249,.76) 100%)',
  thumbBg: 'rgba(255,253,249,.72)',
  thumbBgImage: 'linear-gradient(135deg,rgba(255,251,246,.9) 0%,rgba(255,248,242,.9) 52%,rgba(251,241,232,.9) 100%)',
  thumbBorder: 'rgba(226,151,105,.18)',
  chipBg: 'rgba(233,130,69,.12)',
  chipBorder: 'rgba(233,130,69,.26)',
  chipText: '#9A421F',
  searchBg: 'rgba(51,42,37,.04)',
  searchBorder: 'rgba(226,151,105,.16)',
  searchPlaceholder: '#877971',
  overlay: 'rgba(51,42,37,.35)',
  cardBg: '#FFFDF9',
  cardBgImage: 'linear-gradient(135deg,#F7ECD9 0%,#FFFCF3 46%,#FFF8F2 100%)',
  cardBorder: 'rgba(226,151,105,.16)',
  cardShadow: '0 24px 60px rgba(51,42,37,.16)',
  primaryBg: 'linear-gradient(145deg,#FFF4EA 0%,#FFE8D6 100%)',
  primaryBorder: 'rgba(233,130,69,.28)',
  primaryText: '#9A421F',
  avatarRing: 'rgba(145,99,77,.35)',
  systemRowBg: '#FCF7F2',
  systemRowBorder: 'rgba(226,151,105,.16)',
  ctaHeading: '#332A25',
  ctaBorder: 'linear-gradient(125deg,#F27B3F,#F4AD72)',
  ctaGlow: '0 0 8px rgba(242,123,63,.12),inset 0 0 14px rgba(244,173,114,.06)',
  ctaButtonBg: 'linear-gradient(135deg,#FFC493 0%,#F5A164 100%)',
  ctaButtonText: '#332A25',
  ctaOrbitFrom: '#E98245',
  ctaOrbitTo: '#F0A06D',
  ctaIcon: '#91634D',
  progressTrack: 'rgba(255,255,255,.85)',
  // Slice 6 review: the 2px fill on the light transport bar needs 3:1 as a non-text indicator.
  progressFill: '#FB7F3D', // slice 11 (D-89)
  progressFillImage: 'linear-gradient(90deg,#F97535,#FD8A48)',
  progressThumb: '#FE8446',
  cardGlassBg: 'rgba(255,250,245,.45)',
  overlayScrim: 'rgba(255,253,249,.78)',
  floatShadow: '0 10px 28px rgba(51,42,37,.18),0 2px 8px rgba(51,42,37,.10)',
  glassBg: 'rgba(255,248,241,.44)',
  glassBgClear: 'rgba(255,248,241,.86)',
  glassWarm: 'rgba(233,130,69,.025)',
  glassEdge: 'rgba(226,151,105,.38)',
  glassTop: 'rgba(255,255,255,.65)',
  glassShadow: '0 12px 32px rgba(51,42,37,.10)',
  glassFallback: '#FFF8F1',
  glassFocus: '#9A421F',
  glassIcon: '#91634D',
  glassSection: '#9A421F',
  glassSelectBg: 'rgba(255,255,255,.12)',
  glassSelectBorder: 'rgba(168,124,76,.25)',
  glassCurrentBg: 'rgba(233,130,69,.08)',
  glassCurrentBorder: 'rgba(168,124,76,.25)',
  menuActive: 'linear-gradient(125deg,#E98245 0%,#F0A06D 55%,#F6C5A7 100%)',
  menuActiveHover: 'linear-gradient(125deg,#ED8E54 0%,#F3AA79 55%,#F8CFB5 100%)',
  menuActivePressed: 'linear-gradient(125deg,#DA733A 0%,#E9945F 55%,#EDB28F 100%)',
  menuActiveText: '#332A25',
  menuActiveBorder: 'rgba(246,197,167,.45)',
  destructiveBg: 'rgba(180,90,65,.04)',
  destructiveBorder: 'rgba(154,66,31,.42)',
  destructiveText: '#9A421F',
  modalScrim: 'rgba(51,42,37,.08)',
  errorBg: 'rgba(154,66,31,.08)',
  errorText: '#9A421F',
  toneWarn: '#9A5B00',
  toneSuccess: '#1E7B3E',
  toneDanger: '#B42318',
  toneRecord: '#B42318',
};

export const liveTokens: Record<LiveThemeName, LiveTokens> = { dark, light };
